// Static portfolio data for Tariq Kamal
import {
  Film, Video, Camera, Palette, Wand2, Sparkles,
  TrendingUp, Users, Share2, Target, Layers, MessagesSquare,
  Instagram, Youtube, Radio, Award, MapPin, Mail, Phone, Calendar,
  BarChart3, Aperture, Clapperboard, Compass, Megaphone, PenTool,
} from "lucide-react";

export const PROFILE = {
  name: "Tariq Kamal",
  titles: ["Digital Content Creator", "Visual Storyteller", "Cinematic Travel Filmmaker"],
  location: "Krishnanagar, Nepal",
  email: "tariqkamalbeg@gmail.com",
  phones: ["+977 9764656680", "+91 8127925860"],
  instagram: "tariqkamalbeg",
  instagramUrl: "https://instagram.com/tariqkamalbeg",
  bio: `Hi, I'm Tariq Kamal — a Digital Content Creator and Visual Storyteller dedicated to capturing the world through a cinematic lens. I specialize in transforming travel experiences and everyday moments into compelling short-form cinematic stories that engage audiences and drive measurable results.`,
  bioLong: `By combining premium videography with data-driven social media strategy, I successfully achieved 10 Million+ organic video views in a single month. My goal is to collaborate with global lifestyle brands, tourism boards, and innovative companies to elevate their digital presence through authentic, high-quality visual storytelling.`,
  mission: `I believe every destination, every culture, and every moment deserves to be experienced through meaningful storytelling. My mission is to create cinematic content that connects audiences emotionally while delivering measurable value for brands.`,
  portrait: "https://images.unsplash.com/photo-1776670486766-d4cdfdb5fe33?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHw0fHxjaW5lbWF0aWMlMjBtYWxlJTIwcG9ydHJhaXQlMjBkYXJrJTIwbW9vZHl8ZW58MHx8fHwxNzgzOTg1MTAwfDA&ixlib=rb-4.1.0&q=85",
};

export const HERO_VIDEOS = [
  "https://cdn.plyr.io/static/demo/View_From_A_Blue_Moon_Trailer-720p.mp4",
];

export const HERO_VIDEO = "https://cdn.plyr.io/static/demo/View_From_A_Blue_Moon_Trailer-720p.mp4";
export const HERO_POSTER = "https://images.unsplash.com/photo-1690239575722-8b319e7b7760?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHw0fHxjaW5lbWF0aWMlMjB0cmF2ZWwlMjBmaWxtbWFraW5nJTIwZGFya3xlbnwwfHx8fDE3ODM5ODUxMDV8MA&ixlib=rb-4.1.0&q=85";

export const STATS = [
  { value: "10M+", label: "Organic Monthly Views", suffix: "" },
  { value: "6000+", label: "Growing Community" },
  { value: "2023+", label: "Creating Professional Content" },
];

export const EDUCATION = [
  {
    degree: "Bachelor of Business Administration",
    school: "Integral University, Lucknow",
    year: "2025",
    detail: "Focused on marketing, brand strategy, and digital business fundamentals — foundation for building creator-led brand collaborations.",
  },
];

export const EXPERIENCE = [
  {
    role: "Freelance Digital Content Creator & Visual Storyteller",
    company: "Instagram — @tariqkamalbeg",
    period: "2023 – Present",
    responsibilities: [
      "End-to-end cinematic content production",
      "Professional videography & creative direction",
      "Short-form storytelling for Reels, Shorts & TikTok",
      "Color grading & post production",
      "Viral content creation strategy",
    ],
    achievements: [
      "10 Million+ organic views in 30 days (March 2026)",
      "6,000+ engaged followers built organically",
      "Managed premium brand collaborations",
      "Cinematic travel & lifestyle content series",
      "Executed data-driven growth strategies",
    ],
  },
];

export const SKILLS = {
  Creative: [
    { name: "Cinematic Videography", desc: "Storyboarding, framing, and shooting with a filmmaker's eye.", icon: Clapperboard },
    { name: "Visual Storytelling", desc: "Turning moments into narratives that pull audiences in.", icon: Film },
    { name: "Video Editing", desc: "Precise cuts, rhythm, and pacing that hold attention.", icon: Video },
    { name: "Post Production", desc: "Sound design, transitions, and finishing polish.", icon: Sparkles },
    { name: "Photography", desc: "Composition, light, and mood in every frame.", icon: Camera },
    { name: "Creative Direction", desc: "Guiding tone, style, and identity end-to-end.", icon: Compass },
  ],
  Technical: [
    { name: "Adobe Premiere Pro", desc: "Advanced multicam and timeline mastery.", icon: Aperture },
    { name: "CapCut", desc: "Rapid short-form editing for social virality.", icon: Wand2 },
    { name: "VN Video Editor", desc: "Mobile-first cinematic edits on the go.", icon: PenTool },
    { name: "Adobe Lightroom", desc: "Signature color science and cinematic grading.", icon: Palette },
  ],
  Marketing: [
    { name: "Social Media Analytics", desc: "Reading data to shape better content.", icon: BarChart3 },
    { name: "Viral Content Strategy", desc: "Hooks, formats, and pacing that scale.", icon: TrendingUp },
    { name: "Audience Growth", desc: "Building loyal, engaged communities.", icon: Users },
    { name: "Brand Collaboration", desc: "Aligning creator and brand vision.", icon: Share2 },
    { name: "Influencer Marketing", desc: "Positioning talent for maximum impact.", icon: Megaphone },
    { name: "Trend Analysis", desc: "Spotting cultural moments before they peak.", icon: Radio },
    { name: "Content Scheduling", desc: "Consistency across platforms and time zones.", icon: Calendar },
    { name: "Community Building", desc: "Turning followers into a real audience.", icon: MessagesSquare },
  ],
};

export const SERVICES = [
  { title: "Cinematic Travel Videos", desc: "Immersive destination films that transport viewers.", icon: Compass },
  { title: "Short-form Content", desc: "Reels, Shorts & TikToks engineered for reach.", icon: Video },
  { title: "Brand Commercials", desc: "Premium ad films with a cinematic identity.", icon: Clapperboard },
  { title: "Product Videography", desc: "Hero product films that convert.", icon: Aperture },
  { title: "Video Editing", desc: "Story-driven edits with tight pacing.", icon: Film },
  { title: "Professional Color Grading", desc: "Signature cinematic looks and color science.", icon: Palette },
  { title: "Tourism Campaigns", desc: "Destination marketing for boards and DMOs.", icon: MapPin },
  { title: "Destination Marketing", desc: "Multi-platform campaigns that drive footfall.", icon: Target },
  { title: "UGC Content Creation", desc: "Authentic creator-led content for brands.", icon: Users },
  { title: "Photography", desc: "Editorial and lifestyle stills that scroll-stop.", icon: Camera },
  { title: "Creative Direction", desc: "Owning the vision from brief to delivery.", icon: PenTool },
  { title: "Content Consulting", desc: "Strategy sessions for creators and brands.", icon: Layers },
];

export const FEATURED_PROJECT = {
  title: "30 Days Islamic Content Series",
  category: "Personal Project",
  description:
    "A cinematic 30-day series of short-form videos exploring Islamic thoughts, culture, and visual storytelling. Built with a signature warm-toned color palette, restrained motion, and an editorial narrative structure.",
  process: [
    "Concept & narrative arc across 30 days",
    "Location scouting and shot lists",
    "Cinematic capture with mobile-first framing",
    "Custom color grade and sound design",
    "Publishing cadence tuned to platform analytics",
  ],
  learnings: [
    "Consistency compounds — daily publishing drove exponential reach",
    "Emotion beats production value on short-form",
    "A signature look creates instant brand recognition",
  ],
  metrics: [
    { value: "10M+", label: "Views" },
    { value: "300K+", label: "Engagements" },
    { value: "30", label: "Days" },
    { value: "March 2026", label: "Launched" },
  ],
  cover: "https://i.postimg.cc/08GYQ3YX/Stock-Cake-Sunset-at-Kaaba-1478873-medium.jpg",
  gallery: [
    "https://images.unsplash.com/photo-1535540878298-a155c6d065ef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwxfHxjaW5lbWF0aWMlMjB0cmF2ZWwlMjBmaWxtbWFraW5nJTIwZGFya3xlbnwwfHx8fDE3ODM5ODUxMDV8MA&ixlib=rb-4.1.0&q=85",
    "https://images.unsplash.com/photo-1700159357774-8a367f7febfc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwyfHxjaW5lbWF0aWMlMjB0cmF2ZWwlMjBmaWxtbWFraW5nJTIwZGFya3xlbnwwfHx8fDE3ODM5ODUxMDV8MA&ixlib=rb-4.1.0&q=85",
    "https://images.unsplash.com/photo-1690239575722-8b319e7b7760?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHw0fHxjaW5lbWF0aWMlMjB0cmF2ZWwlMjBmaWxtbWFraW5nJTIwZGFya3xlbnwwfHx8fDE3ODM5ODUxMDV8MA&ixlib=rb-4.1.0&q=85",
  ],
};

export const PORTFOLIO_CATEGORIES = [
  "All",
  "Travel Films",
  "Lifestyle",
  "Cinematic Reels",
  "Brand Content",
  "Photography",
  "Short Films",
];

export const PORTFOLIO_ITEMS = [
  {
    id: "p1",
    title: "Wanderlust — Himalayan Diaries",
    category: "Travel Films",
    client: "Personal",
    image: "https://i.postimg.cc/8pNzpXwC/IMG-9872.jpg",
    link: "https://www.instagram.com/reel/DSpZKbYDFbA/?utm_source=ig_web_copy_link&igsh=MzRlODBiNWFlZA==",
    span: "row-span-2",
  },
  {
    id: "p2",
    title: "Golden Hour — Streets of Lucknow",
    category: "Photography",
    client: "Editorial",
    image: "https://i.postimg.cc/2rZyxW12/IMG-3924.jpg",
    link: "https://www.instagram.com/reel/DTVcRbykuPo/?utm_source=ig_web_copy_link&igsh=MzRlODBiNWFlZA==",
  },
  {
    id: "p3",
    title: "Silhouettes — Short Film",
    category: "Short Films",
    client: "Personal",
    image: "https://i.postimg.cc/GLf3F3rZ/IMG-3925.jpg",
    link: "https://www.instagram.com/reel/DZQD6z8tkUo/?utm_source=ig_web_copy_link&igsh=MzRlODBiNWFlZA==",
  },
  {
    id: "p4",
    title: "Lifestyle Series — Everyday Rituals",
    category: "Lifestyle",
    client: "Personal",
    image: "https://i.postimg.cc/jRhs84tQ/IMG-3926.jpg",
    link: "https://www.instagram.com/reel/DTnFxQnDGzn/?utm_source=ig_web_copy_link&igsh=MzRlODBiNWFlZA==",
    span: "row-span-2",
  },
  {
    id: "p5",
    title: "Skyline — Brand Commercial",
    category: "Brand Content",
    client: "Confidential",
    image: "https://i.postimg.cc/Ph8dGjdZ/IMG-3927.jpg",
    link: "https://www.instagram.com/reel/DUX4NWOjMbq/?utm_source=ig_web_copy_link&igsh=MzRlODBiNWFlZA==",
  },
];

export const NAV_LINKS = [
  { id: "home", label: "Home" },
  { id: "about", label: "About" },
  { id: "experience", label: "Experience" },
  { id: "skills", label: "Skills" },
  { id: "services", label: "Services" },
  { id: "featured", label: "Featured" },
  { id: "portfolio", label: "Portfolio" },
  { id: "contact", label: "Contact" },
];
